import { TrendColorizer } from 'components/TrendColorizer'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCancelAllSpotState,
  useCancelSpotPendingIds,
  useSpotSymbols,
} from 'store/spot/hooks'
import { OrderDirection, OrderType } from 'store/spot/types'
import { DateFormatter } from 'utils/datetime'
import { isZero, toFixed } from 'utils/number'
import { findSymbolById, findSymbolByName } from 'utils/symbols'
import { calcOrderFilledSize, calcOrderValue } from 'hooks/useSizeCalculations'

import {
  MobileItemTitle,
  MobileItemData,
  MobileItemFlex,
  MobileColumnItem,
  MobileCardWrap,
  TriggerDone,
  MobileCancelButton,
} from '../styles'
import { StringFormatter } from 'utils/StringFormatter'
import { getStopOrderType } from 'utils/getStopOrderTriggerDirection'
import numbro from 'numbro'
import { useCancelOrder } from 'hooks/spot/useCancelOrder'
import Loading from 'components/Loading'
import { PerpOrderRecord } from 'api/v1/perp'
import { SpotOrderStatus } from 'api/v1/spot'

const MobileRowItem = memo<{
  row: PerpOrderRecord
}>(({ row }) => {
  const { t } = useTranslation()
  const {
    id = 0,
    symbol: symbolName = '',
    direction = OrderDirection.LONG,
    createdAt = 0,
    type = OrderType.LIMIT,
    triggerOn,
    quantity = 0,
    triggerDirection,
    price = 0,
    status,
  } = row ?? {}
  const spotSymbols = useSpotSymbols()
  const symbol = findSymbolByName(spotSymbols, symbolName)
  const {
    baseCurrency = '',
    quoteCurrency = '',
    baseScale = 0,
    quoteScale = 0,
  } = symbol ?? {}
  const cancelAllState = useCancelAllSpotState()

  const getSymbolInfo = (symbolId: number) => {
    const symbol = findSymbolById(spotSymbols, symbolId)
    const {
      baseCurrency = '',
      quoteCurrency = '',
      baseScale = 0,
      quoteScale = 0,
    } = symbol ?? {}

    return {
      baseCurrency,
      quoteCurrency,
      baseScale,
      quoteScale,
    }
  }

  const getFixedValue = (row: PerpOrderRecord) => {
    const {
      type = OrderType.LIMIT,
      price = 0,
      triggerOn = '',
      quantity = 0,
      symbolId,
    } = row
    const isStopOrder = !isZero(triggerOn)
    const isStopMarket = type === OrderType.MARKET && isStopOrder
    const symbol = getSymbolInfo(symbolId)
    const { quoteScale } = symbol
    const priceOrTriggerPrice = isStopMarket ? Number(triggerOn) : price
    const value = calcOrderValue(String(quantity), priceOrTriggerPrice)
    const fixedValue = toFixed(value, quoteScale)
    return fixedValue
  }

  const getFilledSizeQuantity = (row: PerpOrderRecord) => {
    const { symbolId, quantity = 0, unfilledQuantity = 0 } = row
    const symbol = getSymbolInfo(symbolId)
    const { baseScale } = symbol
    const filledSize = calcOrderFilledSize(quantity, unfilledQuantity)
    const fixedQuantity = toFixed(quantity, baseScale)
    return {
      filledSize,
      fixedQuantity,
    }
  }

  const datetime = DateFormatter.toISODateTime(createdAt)

  const isStopOrder = !isZero(triggerOn)
  const orderType = isStopOrder
    ? getStopOrderType(type, direction, triggerDirection)
    : StringFormatter.startCase(type)

  const fixedQuantity = toFixed(quantity, baseScale)

  const isStopMarket = type === OrderType.MARKET && isStopOrder
  const priceOrTriggerPrice = isStopMarket ? Number(triggerOn) : price
  const fixedPrice = toFixed(priceOrTriggerPrice, quoteScale)

  const priceString = isStopMarket ? 'Market' : `${fixedPrice} ${quoteCurrency}`

  const { filledSize } = getFilledSizeQuantity(row)

  const filled = numbro(Number(filledSize) / Number(quantity)).format({
    output: 'percent',
    mantissa: 2,
  })
  const total = getFixedValue(row)

  const cancelSpotPendingIds = useCancelSpotPendingIds()
  const isExist = cancelSpotPendingIds?.find((item) => item === id)

  const cancelOrder = useCancelOrder()
  return (
    <MobileCardWrap>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Date</MobileItemTitle>
          <MobileItemData>{datetime}</MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Pair</MobileItemTitle>
          <MobileItemData>
            {baseCurrency}/{quoteCurrency}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Type</MobileItemTitle>
          <MobileItemData>{orderType}</MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Side</MobileItemTitle>
          <MobileItemData>
            <TrendColorizer
              trend={direction === OrderDirection.LONG}
              value={
                direction === OrderDirection.LONG
                  ? t('user-order-table-content-buy')
                  : t('user-order-table-content-sell')
              }
            />
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Price</MobileItemTitle>
          <MobileItemData>{priceString}</MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Amount</MobileItemTitle>
          <MobileItemData>
            {fixedQuantity} {baseCurrency}
          </MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
      <MobileItemFlex>
        <MobileColumnItem flex="1.3">
          <MobileItemTitle>Filled</MobileItemTitle>
          <MobileItemData>{filled}</MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem>
          <MobileItemTitle>Total</MobileItemTitle>
          <MobileItemData>
            {total} {quoteCurrency}
          </MobileItemData>
        </MobileColumnItem>
        <MobileColumnItem className="last">
          <MobileItemTitle>Trigger Conditions</MobileItemTitle>
          <MobileItemData>
            {isZero(triggerOn) ? (
              '-'
            ) : (
              <>
                Last Price{' '}
                {triggerDirection === OrderDirection.LONG ? '>=' : '<='}{' '}
                {triggerOn}
                {status === SpotOrderStatus.PENDING && <TriggerDone />}
              </>
            )}
          </MobileItemData>
        </MobileColumnItem>
      </MobileItemFlex>
      <MobileCancelButton
        className={isExist || cancelAllState ? 'disabled' : ''}
        onClick={() => {
          if (isExist || cancelAllState) return
          cancelOrder(row)
        }}
      >
        {isExist ? <Loading /> : 'Cancel'}
      </MobileCancelButton>
    </MobileCardWrap>
  )
})

export default MobileRowItem
